import { useCallback, useEffect, useMemo, useState } from "react";
import { canvasApi } from "../api/client";
import type { CanvasRevision, CanvasWorkspace } from "../types";
import { buildTree, isEditable } from "../lib/canvasTree";
import {
  deleteChange, draftChanges, renameChanges, shaFor, summarize, writeChange,
  type CanvasChange, type Drafts,
} from "../lib/canvasDraft";
import CanvasEditor from "./CanvasEditor";
import CanvasFileTree from "./CanvasFileTree";
import CanvasPreview from "./CanvasPreview";
import "./CanvasPane.css";

const BLANK_ENTRY = "index.html";
const BLANK_SOURCE = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Canvas</title>
    <link rel="stylesheet" href="styles.css" />
  </head>
  <body>
    <h1>Hello canvas</h1>
    <script type="module" src="app.js"></script>
  </body>
</html>
`;

export default function CanvasPane({ chatId, onClose }: { chatId: string; onClose(): void }) {
  const [workspace, setWorkspace] = useState<CanvasWorkspace | null>(null);
  const [revision, setRevision] = useState<CanvasRevision | null>(null);
  const [openPaths, setOpenPaths] = useState<string[]>([]);
  const [activePath, setActivePath] = useState("");
  /** Saved file bodies, keyed by path — the baseline the editor diffs against. */
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Drafts>({});
  const [tab, setTab] = useState<"preview" | "code" | "changes">("preview");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const files = useMemo(() => revision?.files ?? [], [revision]);
  const tree = useMemo(() => buildTree(files), [files]);
  const dirtyPaths = useMemo(() => new Set(Object.keys(drafts)), [drafts]);
  const dirty = dirtyPaths.size > 0;

  const load = useCallback(async () => {
    const rows = await canvasApi.list(chatId);
    const current = rows[rows.length - 1] ?? null;
    setWorkspace(current);
    setRevision(current?.head_revision_id ? await canvasApi.revision(current.id, current.head_revision_id) : null);
  }, [chatId]);

  useEffect(() => {
    setDrafts({});
    setSaved({});
    setOpenPaths([]);
    setActivePath("");
    load().catch(e => setError((e as Error).message));
  }, [chatId, load]);

  /** Fetch a file body once and cache it as the saved baseline. */
  const ensureLoaded = useCallback(
    async (path: string) => {
      if (!workspace || !revision || saved[path] !== undefined) return;
      const file = await canvasApi.file(workspace.id, revision.id, path);
      setSaved(prev => ({ ...prev, [path]: file.content }));
    },
    [workspace, revision, saved]
  );

  const open = useCallback(
    (path: string) => {
      if (!isEditable(path)) return setError(`${path} is not a text file the editor can open.`);
      setError("");
      setActivePath(path);
      setOpenPaths(prev => (prev.includes(path) ? prev : [...prev, path]));
      ensureLoaded(path).catch(e => setError((e as Error).message));
    },
    [ensureLoaded]
  );

  // On a new revision, drop stale bodies but keep the same tabs open so a save
  // (or an assistant edit) doesn't reset where the user was working.
  useEffect(() => {
    if (!revision) return;
    setSaved({});
    const present = new Set(files.map(f => f.path));
    setOpenPaths(prev => prev.filter(p => present.has(p)));
    const preferred =
      (activePath && present.has(activePath) && activePath) ||
      workspace?.entry_path ||
      files.find(f => f.path.endsWith(".html"))?.path ||
      files[0]?.path ||
      "";
    if (preferred) open(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision?.id]);

  const body = (path: string) => drafts[path]?.content ?? saved[path] ?? "";

  const edit = (path: string, next: string) =>
    setDrafts(prev => {
      if (next === saved[path]) {
        const { [path]: _dropped, ...rest } = prev;
        return rest;
      }
      return { ...prev, [path]: { content: next, baseSha: prev[path]?.baseSha ?? shaFor(files, path) } };
    });

  /** Commit a change list as one revision and reload the resulting head. */
  const commit = useCallback(
    async (changes: CanvasChange[], summary: string) => {
      if (!workspace || !changes.length) return;
      setBusy(true);
      setError("");
      try {
        const result = await canvasApi.save(workspace.id, {
          base_revision_id: revision?.id ?? null,
          summary,
          changes,
        });
        setWorkspace(result.workspace);
        setRevision(await canvasApi.revision(workspace.id, result.revision.id));
        setDrafts({});
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [workspace, revision]
  );

  // Every dirty buffer goes out in ONE call: the backend commits a change list
  // atomically, so this is one revision per user intent, not one per file.
  const saveAll = () => void commit(draftChanges(drafts), summarize(draftChanges(drafts)));

  const createFile = () => {
    const path = window.prompt("New file path (e.g. components/Card.jsx)")?.trim();
    if (!path) return;
    if (files.some(f => f.path === path)) return setError(`${path} already exists.`);
    void commit([writeChange(path, "", null)], `Create ${path}`).then(() => setActivePath(path));
  };

  const renameFile = (from: string) => {
    const to = window.prompt("Rename to", from)?.trim();
    if (!to || to === from) return;
    void (async () => {
      await ensureLoaded(from);
      const content = drafts[from]?.content ?? saved[from];
      if (content === undefined) {
        const fetched = await canvasApi.file(workspace!.id, revision!.id, from);
        return commit(renameChanges(from, to, fetched.content, fetched.sha256), `Rename ${from} → ${to}`);
      }
      return commit(renameChanges(from, to, content, shaFor(files, from)), `Rename ${from} → ${to}`);
    })().catch(e => setError((e as Error).message));
  };

  const deleteFile = (path: string) => {
    if (files.length === 1) return setError("A canvas must keep at least one file.");
    if (!window.confirm(`Delete ${path}? This is recorded as a new revision and can be restored.`)) return;
    void commit([deleteChange(path, shaFor(files, path))], `Delete ${path}`);
  };

  const createBlank = async () => {
    try {
      const created = await canvasApi.create(chatId, {
        title: "Untitled canvas",
        framework: "static",
        entry_path: BLANK_ENTRY,
      });
      const result = await canvasApi.save(created.id, {
        base_revision_id: null,
        summary: "Create canvas",
        changes: [
          writeChange(BLANK_ENTRY, BLANK_SOURCE, null),
          writeChange("styles.css", "body { font-family: system-ui; margin: 2rem; }\n", null),
          writeChange("app.js", "console.log('canvas ready');\n", null),
        ],
      });
      setWorkspace(result.workspace);
      setRevision(await canvasApi.revision(created.id, result.revision.id));
      setTab("code");
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <aside className="canvas-pane" aria-label="Code canvas">
      <header>
        <strong>{workspace?.title || "Canvas"}</strong>
        <button className="btn btn-sm" onClick={() => void createBlank()}>New blank canvas (manual)</button>
        <button className="btn btn-sm" onClick={onClose}>Close</button>
      </header>
      {error && <p className="canvas-error" role="alert">{error}</p>}
      {!workspace ? (
        <p className="canvas-empty">
          Ask for a website in Chat. Use “New blank canvas (manual)” only when you plan to paste or write source yourself.
        </p>
      ) : (
        <>
          <nav className="canvas-tabs" aria-label="Canvas views">
            <button className={tab === "preview" ? "active" : ""} onClick={() => setTab("preview")}>Preview</button>
            <button className={tab === "code" ? "active" : ""} onClick={() => setTab("code")}>
              Code{dirty ? ` (${dirtyPaths.size}●)` : ""}
            </button>
            <button className={tab === "changes" ? "active" : ""} onClick={() => setTab("changes")}>Changes</button>
          </nav>

          {tab === "preview" && (
            <CanvasPreview
              workspaceId={workspace.id}
              revisionId={revision?.id ?? null}
              dirty={dirty}
              onError={setError}
            />
          )}

          {tab === "code" && (
            <section className="canvas-code">
              <div className="canvas-explorer">
                <div className="canvas-explorer-head">
                  <span>Files</span>
                  <button type="button" className="btn btn-sm" onClick={createFile} disabled={busy}>+ New file</button>
                </div>
                <CanvasFileTree
                  nodes={tree}
                  activePath={activePath}
                  entryPath={workspace.entry_path}
                  dirtyPaths={dirtyPaths}
                  onOpen={open}
                  onRename={renameFile}
                  onDelete={deleteFile}
                />
              </div>
              <div className="canvas-workarea">
                <div className="canvas-file-tabs" role="tablist">
                  {openPaths.map(path => (
                    <span key={path} className={`canvas-file-tab${path === activePath ? " active" : ""}`}>
                      <button type="button" role="tab" aria-selected={path === activePath} onClick={() => open(path)}>
                        {path.split("/").pop()}
                        {dirtyPaths.has(path) && <em className="canvas-tree-dirty">●</em>}
                      </button>
                      <button
                        type="button"
                        className="canvas-file-tab-close"
                        aria-label={`Close ${path}`}
                        onClick={() => {
                          setOpenPaths(prev => prev.filter(p => p !== path));
                          if (path === activePath) setActivePath(openPaths.find(p => p !== path) ?? "");
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {activePath ? (
                  <CanvasEditor
                    path={activePath}
                    value={body(activePath)}
                    onChange={next => edit(activePath, next)}
                    onSave={saveAll}
                  />
                ) : (
                  <p className="canvas-empty">Select a file to edit.</p>
                )}
                <div className="canvas-code-actions">
                  <button className="btn btn-primary" onClick={saveAll} disabled={!dirty || busy}>
                    {dirty ? `Save ${dirtyPaths.size} file${dirtyPaths.size === 1 ? "" : "s"}` : "Saved"}
                  </button>
                </div>
              </div>
            </section>
          )}

          {tab === "changes" && (
            <section className="canvas-changes">
              <p>{revision ? `v${revision.sequence} · ${revision.summary || "Saved revision"}` : "Unsaved draft"}</p>
              <p className="canvas-meta">
                {files.length} file{files.length === 1 ? "" : "s"} · entry {workspace.entry_path || "—"} · {workspace.framework}
              </p>
              {revision && (
                <a className="btn btn-sm" href={canvasApi.archiveUrl(workspace.id, revision.id)}>Download codebase</a>
              )}
            </section>
          )}
        </>
      )}
    </aside>
  );
}
